/**
 * fetch wrapper that attaches the Fabric ID token.
 *
 * Copied from Fabric, as every module's is. The only per-module difference is the
 * base URL — here it points at `hindsight-ingest`, which verifies the same
 * Fabric-issued token. That is what makes one household sign-in reach every
 * portal: every client asks the same project who you are.
 */
import { getIdToken } from "./auth";

const BASE = import.meta.env.VITE_HINDSIGHT_API_URL ?? "";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function call<T>(method: string, path: string): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new ApiError(401, "Not signed in");

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    // The API answers errors as {error}. Fall back to the status text when a
    // proxy or a cold start returns HTML instead, so the UI never shows
    // "Unexpected token < in JSON".
    const detail = await res
      .json()
      .then((j: { error?: string }) => j.error)
      .catch(() => null);
    throw new ApiError(res.status, detail ?? res.statusText);
  }

  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => call<T>("GET", path),
};
